import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon, Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';
import { useToast } from 'OtherReusableFunctionality/ToastContext';

const getSizeList = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.sizes)) return data.sizes;
  if (Array.isArray(data?.ProductSize)) return data.ProductSize;
  if (Array.isArray(data?.product_sizes)) return data.product_sizes;
  if (Array.isArray(data?.product_size)) return data.product_size;
  return [];
};

const getSizeId = (sizeItem) => sizeItem?.id ?? sizeItem?.product_size_id ?? sizeItem?.size_id;

const emptySizeForm = {
  size: '',
  gst: '',
  unit_of_measure: '',
  purchase_unit_of_measure: '',
  farmer_price: '',
  mrp: '',
  sequence: '',
  sku: ''
};

const AddProductSizeModal = ({ open, onClose, product, onSizeAdded }) => {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [existingSizes, setExistingSizes] = useState([]);
  const [loadingSizes, setLoadingSizes] = useState(false);
  const [editingSizeId, setEditingSizeId] = useState(null);
  const [deletingSizeId, setDeletingSizeId] = useState(null);
  const [sizeToDelete, setSizeToDelete] = useState(null);
  const [editForm, setEditForm] = useState(emptySizeForm);
  const [formData, setFormData] = useState(emptySizeForm);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchMeasureUnits = async () => {
    setLoadingUnits(true);
    try {
      const response = await apiService.get(API_ENDPOINTS.GET_measure_units);
      if (response?.success) {
        setUnits(response.data?.MeasureUnits || []);
      } else {
        setUnits([]);
      }
    } catch (fetchError) {
      console.error('Error fetching measure units:', fetchError);
      setUnits([]);
    } finally {
      setLoadingUnits(false);
    }
  };

  const fetchProductSizes = async () => {
    if (!product?.id) return;

    setLoadingSizes(true);
    try {
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_product_size_by_id}?product_id=${product.id}`
      );
      if (response?.success) {
        setExistingSizes(getSizeList(response));
      } else {
        setExistingSizes([]);
      }
    } catch (fetchError) {
      console.error('Error fetching product sizes:', fetchError);
      setExistingSizes([]);
    } finally {
      setLoadingSizes(false);
    }
  };

  useEffect(() => {
    if (!open || !product) return;

    setFormData({
      size: '',
      gst: product.gst?.toString() || '',
      unit_of_measure: product.unit_of_measure || '',
      purchase_unit_of_measure: product.purchase_unit_of_measure || product.unit_of_measure || '',
      farmer_price: '',
      mrp: '',
      sequence: '',
      sku: product.sku || ''
    });
    setError('');
    setExistingSizes([]);
    setEditingSizeId(null);
    setDeletingSizeId(null);
    setSizeToDelete(null);
    setEditForm(emptySizeForm);
    setShowAddForm(false);
    fetchMeasureUnits();
    fetchProductSizes();
  }, [open, product]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateSizeForm = (data) => {
    const required = [
      'size',
      'gst',
      'unit_of_measure',
      'purchase_unit_of_measure',
      'farmer_price',
      'mrp',
      'sku'
    ];
    const missing = required.filter((field) => !data[field]?.toString().trim());

    if (missing.length > 0) {
      setError(`Please fill in all required fields: ${missing.join(', ')}`);
      return false;
    }

    if (parseFloat(data.farmer_price) <= 0 || parseFloat(data.mrp) <= 0) {
      setError('Prices must be greater than 0');
      return false;
    }

    return true;
  };

  const validateForm = () => validateSizeForm(formData);

  const handleStartEdit = (sizeItem) => {
    setShowAddForm(false);
    setEditingSizeId(getSizeId(sizeItem));
    setEditForm({
      size: sizeItem.size?.toString() || '',
      gst: sizeItem.gst?.toString() || '',
      unit_of_measure: sizeItem.unit_of_measure || '',
      purchase_unit_of_measure: sizeItem.purchase_unit_of_measure || sizeItem.unit_of_measure || '',
      farmer_price: sizeItem.farmer_price?.toString() || '',
      mrp: sizeItem.mrp?.toString() || '',
      sequence: sizeItem.sequence?.toString() || '',
      sku: sizeItem.sku || ''
    });
    setError('');
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setFormData({
      size: '',
      gst: product?.gst?.toString() || '',
      unit_of_measure: product?.unit_of_measure || '',
      purchase_unit_of_measure: product?.purchase_unit_of_measure || product?.unit_of_measure || '',
      farmer_price: '',
      mrp: '',
      sequence: '',
      sku: product?.sku || ''
    });
    setError('');
  };

  const handleOpenAddForm = () => {
    setEditingSizeId(null);
    setShowAddForm(true);
    setError('');
  };

  const handleCancelEdit = () => {
    setEditingSizeId(null);
    setEditForm(emptySizeForm);
    setError('');
  };

  const handleEditInputChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleUpdateSize = async (sizeItem) => {
    const sizeId = getSizeId(sizeItem);
    if (!product?.id || !sizeId || !validateSizeForm(editForm)) return;

    setIsSubmitting(true);
    setError('');

    try {
      const submitData = new FormData();
      submitData.append('size_id', sizeId);
      submitData.append('product_id', product.id);
      submitData.append('size', editForm.size);
      submitData.append('gst', editForm.gst);
      submitData.append('unit_of_measure', editForm.unit_of_measure);
      submitData.append('purchase_unit_of_measure', editForm.purchase_unit_of_measure);
      submitData.append('farmer_price', editForm.farmer_price);
      submitData.append('mrp', editForm.mrp);
      submitData.append('sequence', editForm.sequence);
      submitData.append('sku', editForm.sku);

      const response = await apiService.putMultipart(
        API_ENDPOINTS.PUT_update_product_size,
        submitData
      );

      if (response?.success) {
        toast.showSuccess(response?.message || 'Product size updated successfully!');
        handleCancelEdit();
        await fetchProductSizes();
        onSizeAdded?.();
      } else {
        setError(response?.message || 'Failed to update product size');
      }
    } catch (updateError) {
      console.error('Error updating product size:', updateError);
      setError(updateError?.message || 'An error occurred while updating product size');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSizeLabel = (sizeItem) =>
    `${sizeItem?.size ?? ''} ${sizeItem?.unit_of_measure || product?.unit_of_measure || ''}`.trim();

  const handleDeleteSize = (sizeItem) => {
    const sizeId = getSizeId(sizeItem);
    if (!sizeId) return;
    setSizeToDelete(sizeItem);
  };

  const handleCloseDeleteConfirm = () => {
    setSizeToDelete(null);
  };

  const handleConfirmDeleteSize = async () => {
    const sizeItem = sizeToDelete;
    const sizeId = getSizeId(sizeItem);
    if (!sizeId || !product?.id) return;

    setDeletingSizeId(sizeId);
    setError('');

    try {
      const token = apiService.getAccessToken();
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}${API_ENDPOINTS.DELETE_product_size}?size_id=${sizeId}&product=${product.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const result = await response.json();

      if (response.ok && result?.success !== false) {
        toast.showSuccess(result?.message || 'Product size deleted successfully!');
        if (editingSizeId === sizeId) {
          handleCancelEdit();
        }
        setSizeToDelete(null);
        setExistingSizes((prev) => prev.filter((item) => getSizeId(item) !== sizeId));
        onSizeAdded?.();
      } else {
        setError(result?.message || 'Failed to delete product size');
      }
    } catch (deleteError) {
      console.error('Error deleting product size:', deleteError);
      setError(deleteError?.message || 'An error occurred while deleting product size');
    } finally {
      setDeletingSizeId(null);
    }
  };

  const handleSubmit = async () => {
    if (!product || !validateForm()) return;

    setIsSubmitting(true);
    setError('');

    try {
      const submitData = new FormData();
      submitData.append('product_id', product.id);
      submitData.append('size', formData.size);
      submitData.append('gst', formData.gst);
      submitData.append('unit_of_measure', formData.unit_of_measure);
      submitData.append('purchase_unit_of_measure', formData.purchase_unit_of_measure);
      submitData.append('farmer_price', formData.farmer_price);
      submitData.append('mrp', formData.mrp);
      submitData.append('sequence', formData.sequence);
      submitData.append('sku', formData.sku);

      const response = await apiService.postMultipart(
        API_ENDPOINTS.POST_add_product_size,
        submitData
      );

      if (response?.success) {
        toast.showSuccess(response?.message || 'Product size added successfully!');
        handleCancelAdd();
        await fetchProductSizes();
        onSizeAdded?.();
      } else {
        setError(response?.message || 'Failed to add product size');
      }
    } catch (submitError) {
      console.error('Error adding product size:', submitError);
      setError(submitError?.message || 'An error occurred while adding product size');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!product) return null;

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Add Size</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage sizes for {product.name}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!showAddForm && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddForm}
              disabled={isSubmitting || loadingSizes}
            >
              Add Size
            </Button>
          </Box>
        )}

        {showAddForm && (
        <Box sx={{ mt: 1, mb: 3, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Add Size
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Size"
                type="number"
                value={formData.size}
                onChange={(e) => handleInputChange('size', e.target.value.replace(/[^\d.]/g, ''))}
                placeholder="e.g. 150"
                inputProps={{ min: 0, step: 'any', onWheel: (e) => e.currentTarget.blur() }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sequence"
                type="number"
                value={formData.sequence}
                onChange={(e) => handleInputChange('sequence', e.target.value)}
                inputProps={{ onWheel: (e) => e.currentTarget.blur() }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="SKU"
                value={formData.sku}
                onChange={(e) => handleInputChange('sku', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="GST (%)"
                type="number"
                value={formData.gst}
                onChange={(e) => handleInputChange('gst', e.target.value)}
                inputProps={{ onWheel: (e) => e.currentTarget.blur() }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Unit of Measure</InputLabel>
                <Select
                  value={formData.unit_of_measure}
                  label="Unit of Measure"
                  onChange={(e) => handleInputChange('unit_of_measure', e.target.value)}
                  disabled={loadingUnits}
                >
                  {units.map((unit) => (
                    <MenuItem key={unit.id || unit.name} value={unit.name}>
                      {unit.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Purchase Unit of Measure</InputLabel>
                <Select
                  value={formData.purchase_unit_of_measure}
                  label="Purchase Unit of Measure"
                  onChange={(e) => handleInputChange('purchase_unit_of_measure', e.target.value)}
                  disabled={loadingUnits}
                >
                  {units.map((unit) => (
                    <MenuItem key={`purchase-${unit.id || unit.name}`} value={unit.name}>
                      {unit.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Farmer Price"
                type="number"
                value={formData.farmer_price}
                onChange={(e) => handleInputChange('farmer_price', e.target.value)}
                inputProps={{ onWheel: (e) => e.currentTarget.blur() }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="MRP"
                type="number"
                value={formData.mrp}
                onChange={(e) => handleInputChange('mrp', e.target.value)}
                inputProps={{ onWheel: (e) => e.currentTarget.blur() }}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button onClick={handleCancelAdd} color="inherit" disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              {isSubmitting ? 'Saving...' : 'Save Size'}
            </Button>
          </Box>
        </Box>
        )}

        {loadingSizes ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : existingSizes.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No sizes found for this product yet.
          </Alert>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ mb: 2, borderRadius: 2 }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Size
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>MRP</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Farmer Price</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>SKU</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>GST (%)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Sequence</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {existingSizes.map((sizeItem) => {
                  const sizeId = getSizeId(sizeItem);
                  const isEditing = editingSizeId === sizeId;

                  if (isEditing) {
                    return (
                      <TableRow key={sizeId || `${sizeItem.size}-${sizeItem.sku}`}>
                        <TableCell colSpan={7} sx={{ py: 2, bgcolor: 'action.hover' }}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            Edit Size - {sizeItem.size} {sizeItem.unit_of_measure || product.unit_of_measure || ''}
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                required
                                label="Size"
                                type="number"
                                value={editForm.size}
                                onChange={(e) => handleEditInputChange('size', e.target.value.replace(/[^\d.]/g, ''))}
                                inputProps={{ min: 0, step: 'any', onWheel: (e) => e.currentTarget.blur() }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Sequence"
                                type="number"
                                value={editForm.sequence}
                                onChange={(e) => handleEditInputChange('sequence', e.target.value)}
                                inputProps={{ onWheel: (e) => e.currentTarget.blur() }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                required
                                label="SKU"
                                value={editForm.sku}
                                onChange={(e) => handleEditInputChange('sku', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                required
                                label="GST (%)"
                                type="number"
                                value={editForm.gst}
                                onChange={(e) => handleEditInputChange('gst', e.target.value)}
                                inputProps={{ onWheel: (e) => e.currentTarget.blur() }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth required>
                                <InputLabel>Unit of Measure</InputLabel>
                                <Select
                                  value={editForm.unit_of_measure}
                                  label="Unit of Measure"
                                  onChange={(e) => handleEditInputChange('unit_of_measure', e.target.value)}
                                  disabled={loadingUnits}
                                >
                                  {units.map((unit) => (
                                    <MenuItem key={unit.id || unit.name} value={unit.name}>
                                      {unit.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth required>
                                <InputLabel>Purchase Unit of Measure</InputLabel>
                                <Select
                                  value={editForm.purchase_unit_of_measure}
                                  label="Purchase Unit of Measure"
                                  onChange={(e) => handleEditInputChange('purchase_unit_of_measure', e.target.value)}
                                  disabled={loadingUnits}
                                >
                                  {units.map((unit) => (
                                    <MenuItem key={`edit-purchase-${unit.id || unit.name}`} value={unit.name}>
                                      {unit.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                required
                                label="Farmer Price"
                                type="number"
                                value={editForm.farmer_price}
                                onChange={(e) => handleEditInputChange('farmer_price', e.target.value)}
                                inputProps={{ onWheel: (e) => e.currentTarget.blur() }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                required
                                label="MRP"
                                type="number"
                                value={editForm.mrp}
                                onChange={(e) => handleEditInputChange('mrp', e.target.value)}
                                inputProps={{ onWheel: (e) => e.currentTarget.blur() }}
                              />
                            </Grid>
                          </Grid>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                            <Button onClick={handleCancelEdit} color="inherit" disabled={isSubmitting}>
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={() => handleUpdateSize(sizeItem)}
                              disabled={isSubmitting}
                              startIcon={isSubmitting ? <CircularProgress size={16} /> : <SaveIcon />}
                            >
                              Update
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow
                      key={sizeId || `${sizeItem.size}-${sizeItem.sku}`}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {sizeItem.size} {sizeItem.unit_of_measure || product.unit_of_measure || ''}
                      </TableCell>
                      <TableCell align="right">
                        {sizeItem.mrp != null ? sizeItem.mrp : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {sizeItem.farmer_price != null ? sizeItem.farmer_price : '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        {sizeItem.sku || '—'}
                      </TableCell>
                      <TableCell align="center">
                        {sizeItem.gst != null ? sizeItem.gst : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {sizeItem.sequence != null ? sizeItem.sequence : '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Edit">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleStartEdit(sizeItem)}
                              disabled={Boolean(deletingSizeId) || isSubmitting}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteSize(sizeItem)}
                              disabled={deletingSizeId === sizeId || isSubmitting}
                            >
                              {deletingSizeId === sizeId ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Add new sizes or review existing size entries for this product.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end' }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={Boolean(sizeToDelete)} onClose={handleCloseDeleteConfirm} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Delete Size</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary">
          Are you sure you want to delete size{' '}
          <strong>
            {sizeToDelete ? getSizeLabel(sizeToDelete) : 'this item'}
          </strong>
          ?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCloseDeleteConfirm} color="inherit" disabled={Boolean(deletingSizeId)}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmDeleteSize}
          color="error"
          variant="contained"
          disabled={Boolean(deletingSizeId)}
          startIcon={deletingSizeId ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AddProductSizeModal;
